import { Link, useParams, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowLeft, FileText, Printer } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { ApiError } from "@/shared/lib/apiClient";
import { Breadcrumbs } from "../components/Breadcrumbs";
import {
  fetchAuditorAuditReport,
  type AuditReportDetail,
} from "../api/auditorApi";
import { getAuthToken, getStoredAccountType } from "../auth";
import { cn } from "@/shared/lib/cn";

function formatAmount(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(value);
}

function formatDate(value: string) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getDashboardHomeHref() {
  const accountType = getStoredAccountType();
  if (accountType === "auditor") return "/dashboard/auditor";
  if (accountType === "hospital") return "/dashboard/hospital";
  if (accountType === "enterprise") return "/dashboard/enterprise";
  return "/dashboard/admin";
}

const statusBadgeClass = (status: string) => {
  if (status === "Approved") return "bg-accent/15 text-accent";
  if (status === "Rejected") return "bg-danger-soft text-danger";
  return "bg-card-muted text-muted-foreground";
};

export function AuditReportDetailPage() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const reportId = Number.parseInt(params.id ?? "", 10);
  const source = (searchParams.get("source") as "audit" | "audit_reports" | null) ?? undefined;
  const { t } = useTranslation();
  const labels = t.safecheck.auditReports.detail;

  const [report, setReport] = useState<AuditReportDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!reportId || !getAuthToken()) {
      setLoading(false);
      setError(labels.notFound);
      return;
    }

    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchAuditorAuditReport(reportId, source);
        if (!cancelled) setReport(data);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof ApiError ? err.message : labels.loadError,
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [reportId, source, labels.loadError, labels.notFound]);

  if (loading) {
    return (
      <div className="rounded-xl border border-border bg-card px-6 py-10 text-center text-muted-foreground">
        {labels.loading}
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="space-y-4">
        <Breadcrumbs
          homeHref={getDashboardHomeHref()}
          items={[
            { label: t.safecheck.auditReports.list.title, href: "/dashboard/audit-reports" },
            { label: labels.title },
          ]}
        />
        <div className="rounded-xl border border-danger/20 bg-danger-soft px-4 py-3 text-sm text-danger">
          {error ?? labels.notFound}
        </div>
        <Link
          to="/dashboard/audit-reports"
          className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
        >
          <ArrowLeft size={16} />
          {labels.backToList}
        </Link>
      </div>
    );
  }

  const diffClass =
    Math.abs(report.difference) < 0.01 ? "text-accent" : "text-danger";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <Breadcrumbs
          homeHref={getDashboardHomeHref()}
          items={[
            { label: t.safecheck.auditReports.list.title, href: "/dashboard/audit-reports" },
            { label: `${labels.title} #${report.id}` },
          ]}
        />
        <div className="flex flex-wrap gap-2">
          <Link
            to="/dashboard/audit-reports"
            className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-card-muted"
          >
            <ArrowLeft size={16} />
            {labels.backToList}
          </Link>
          <Link
            to={`/dashboard/invoices/${report.invoice_id}`}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            <FileText size={16} />
            {labels.viewInvoice}
          </Link>
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-card-muted print:hidden"
          >
            <Printer size={16} />
            {labels.print}
          </button>
        </div>
      </div>

      <div className="card-shadow rounded-2xl border border-border bg-card p-6">
        <h1 className="text-xl font-semibold text-foreground">
          {labels.title} #{report.id}
        </h1>
        <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              {labels.auditDate}
            </p>
            <p className="mt-1 font-medium">{formatDate(report.audit_date)}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              {labels.invoice}
            </p>
            <Link
              to={`/dashboard/invoices/${report.invoice_id}`}
              className="mt-1 inline-block font-medium text-primary hover:underline"
            >
              #{report.invoice_number}
            </Link>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              {labels.status}
            </p>
            <span
              className={cn(
                "mt-1 inline-flex rounded-full px-2.5 py-1 text-xs font-medium",
                statusBadgeClass(report.status),
              )}
            >
              {report.status_label}
            </span>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              {labels.auditor}
            </p>
            <p className="mt-1 font-medium">{report.auditor_name}</p>
            {report.auditor_email && (
              <p className="text-sm text-muted-foreground">{report.auditor_email}</p>
            )}
          </div>
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              {labels.hospital}
            </p>
            <p className="mt-1">{report.hospital_name}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              {labels.company}
            </p>
            <p className="mt-1">{report.company_name}</p>
          </div>
        </div>
      </div>

      <div className="card-shadow rounded-2xl border border-border bg-card p-6">
        <h2 className="text-lg font-semibold">{labels.financialAnalysis}</h2>
        <div className="mt-4 grid gap-4 md:grid-cols-3">
          <div className="rounded-xl border border-border p-4 text-center">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              {labels.originalAmount}
            </p>
            <p className="mt-2 text-2xl font-semibold text-primary">
              {formatAmount(report.original_amount)}
            </p>
          </div>
          <div className="rounded-xl border border-border p-4 text-center">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              {labels.basketTotal}
            </p>
            <p className="mt-2 text-2xl font-semibold text-accent">
              {formatAmount(report.basket_total)}
            </p>
          </div>
          <div className="rounded-xl border border-border p-4 text-center">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              {labels.difference}
            </p>
            <p className={cn("mt-2 text-2xl font-semibold", diffClass)}>
              {formatAmount(Math.abs(report.difference))}
            </p>
          </div>
        </div>
        {report.approval_note && (
          <div
            className={cn(
              "mt-4 rounded-xl px-4 py-3 text-sm",
              report.status === "Approved"
                ? "border border-accent/20 bg-accent/10 text-accent-foreground"
                : "border border-danger/20 bg-danger-soft text-danger",
            )}
          >
            <strong>{labels.note}:</strong> {report.approval_note}
          </div>
        )}
      </div>

      {report.basket.length > 0 && (
        <div className="card-shadow rounded-2xl border border-border bg-card p-6">
          <h2 className="text-lg font-semibold">
            {labels.basketTitle} ({report.basket.length})
          </h2>
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-card-muted/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">#</th>
                  <th className="px-4 py-3">{labels.basketCode}</th>
                  <th className="px-4 py-3">{labels.basketLabel}</th>
                  <th className="px-4 py-3 text-right">{labels.basketCost}</th>
                </tr>
              </thead>
              <tbody>
                {report.basket.map((item, index) => (
                  <tr key={`${item.code}-${index}`} className="border-t border-border/70">
                    <td className="px-4 py-3">{index + 1}</td>
                    <td className="px-4 py-3">
                      <code className="rounded bg-card-muted px-2 py-1 text-xs">{item.code}</code>
                    </td>
                    <td className="px-4 py-3">{item.libelle}</td>
                    <td className="px-4 py-3 text-right">{formatAmount(item.cout)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-border bg-card-muted/40 font-semibold">
                  <td colSpan={3} className="px-4 py-3 text-right">
                    {labels.total}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {formatAmount(report.basket_total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {report.diagnosis_code && (
        <div className="card-shadow rounded-2xl border border-border bg-card p-6">
          <h2 className="text-lg font-semibold">{labels.diagnosis}</h2>
          <p className="mt-2">
            <code className="rounded bg-card-muted px-2 py-1 text-sm">{report.diagnosis_code}</code>
          </p>
        </div>
      )}

      <div className="card-shadow rounded-2xl border border-border bg-card p-6">
        <h2 className="text-lg font-semibold">{labels.reportContent}</h2>
        <div className="mt-4 whitespace-pre-wrap rounded-xl border border-border bg-card-muted/30 p-4 text-sm leading-relaxed">
          {report.report || labels.noReportContent}
        </div>
      </div>
    </div>
  );
}
